import { useState } from 'react';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function AboutPage() {
  const [formSuccess, setFormSuccess] = useState(false);
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.elements.namedItem('phone_alt') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormSuccess(true);
      return;
    }

    setSubmitting(true);
    setFormError('');
    const formData = new FormData(form);
    const params = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key !== 'phone_alt') {
        params.append(key, value as string);
      }
    });

    fetch('https://readdy.ai/api/form/d9tgt1lca23r0tr1ait0', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: params.toString(),
    })
      .then(async (res) => {
        const text = await res.text();
        let parsed: any;
        try { parsed = JSON.parse(text); } catch { parsed = {}; }
        if (res.ok && parsed?.code === 'OK') {
          setFormSuccess(true);
          form.reset();
        } else {
          throw new Error(parsed?.meta?.message || parsed?.meta?.detail || text || '提交失败');
        }
      })
      .catch((err) => {
        setFormError(err.message || '提交失败，请稍后重试');
      })
      .finally(() => setSubmitting(false));
  };

  const pageTitle = '关于我们 | 星空影院在线观看免费';
  const pageDesc = '星空影院品牌介绍，了解星空影院的品牌故事、核心优势和用户口碑。星空影院在线观看免费，海量高清影视内容随时畅享。如有问题欢迎联系我们。';
  const pageKeywords = '星空影院,关于星空影院,星空影院品牌,星空影院联系方式,星空影院在线观看免费,免费在线观看电影';

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于星空影院',
    description: '星空影院是一个高品质影视内容聚合平台，致力于为用户提供海量高清电影、热播剧集和精彩综艺节目。无需注册、无需付费，随时随地畅享优质观影体验。',
    url: 'https://yukafile.github.io/about',
    isPartOf: {
      '@type': 'WebSite',
      name: '星空影院',
      url: 'https://yukafile.github.io',
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: 'https://yukafile.github.io/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: '关于我们',
        item: 'https://yukafile.github.io/about',
      },
    ],
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '星空影院是否需要注册才能观看？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '不需要。星空影院在线观看免费，所有影片无需注册、无需付费即可直接观看。我们致力于为用户提供最便捷的观影体验。',
        },
      },
      {
        '@type': 'Question',
        name: '星空影院支持哪些设备播放？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '星空影院支持手机、平板、电脑等多种设备播放，画质清晰流畅，自适应不同屏幕尺寸，让您随时随地畅享高清影视内容。',
        },
      },
      {
        '@type': 'Question',
        name: '星空影院的影片更新频率如何？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '星空影院每日更新最新电影和热播剧集，确保您第一时间观看到最新上映的精彩内容。我们会持续追踪全球热门影视动态。',
        },
      },
      {
        '@type': 'Question',
        name: '星空影院有哪些影片类型？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '星空影院涵盖动作、喜剧、剧情、科幻、恐怖、爱情、动画、纪录片等多种类型，同时还有海量热播剧集和综艺节目供您选择。',
        },
      },
    ],
  };

  const features = [
    {
      icon: 'ri-movie-2-fill',
      title: '海量资源',
      desc: '涵盖数万部电影、剧集、综艺节目，类型丰富，满足不同观众的口味和需求。',
    },
    {
      icon: 'ri-4k-fill',
      title: '高清画质',
      desc: '支持超清画质播放，画面细腻流畅，为您带来影院级的观影体验。',
    },
    {
      icon: 'ri-device-line',
      title: '多端兼容',
      desc: '手机、平板、电脑均可流畅播放，自适应屏幕，随时随地想看就看。',
    },
    {
      icon: 'ri-shield-check-line',
      title: '安全免费',
      desc: '无需注册、无需付费，无广告干扰，安全可靠，保护您的隐私安全。',
    },
    {
      icon: 'ri-speed-up-line',
      title: '极速加载',
      desc: '采用先进的CDN加速技术，影片秒加载，告别缓冲烦恼，流畅不卡顿。',
    },
    {
      icon: 'ri-refresh-line',
      title: '每日更新',
      desc: '片库每日持续更新，最新热映大片、热播剧集第一时间为您呈上。',
    },
  ];

  const testimonials = [
    {
      name: '王先生',
      avatar: 'https://readdy.ai/api/search-image?query=Professional%20Chinese%20man%20portrait%20headshot%2C%20friendly%20smile%2C%20neutral%20warm%20background%2C%20clean%20professional%20photo%20style&width=120&height=120&seq=avatar-01&orientation=squarish',
      role: '资深影迷',
      comment: '星空影院的片源真的太丰富了！我想看的电影这里基本都有，而且画质特别清晰，加载速度也很快，已经推荐给身边所有朋友了。',
    },
    {
      name: '李女士',
      avatar: 'https://readdy.ai/api/search-image?query=Professional%20Chinese%20woman%20portrait%20headshot%2C%20warm%20smile%2C%20neutral%20light%20background%2C%20professional%20photo%20style%2C%20friendly%20expression&width=120&height=120&seq=avatar-02&orientation=squarish',
      role: '剧集爱好者',
      comment: '作为一个追剧狂魔，星空影院满足了我所有的需求。更新快、画质好，而且完全不收费，真的是良心平台！每天下班回家第一件事就是打开星空影院。',
    },
    {
      name: '张同学',
      avatar: 'https://readdy.ai/api/search-image?query=Young%20Chinese%20student%20portrait%20headshot%2C%20bright%20smile%2C%20casual%20style%2C%20clean%20light%20background%2C%20natural%20photo%20style&width=120&height=120&seq=avatar-03&orientation=squarish',
      role: '大学生',
      comment: '作为学生党，免费又能看高清的平台真的太友好了！周末和室友一起看电影已经成为我们的固定节目，星空影院陪伴了我们无数个欢乐的夜晚。',
    },
  ];

  return (
    <>
      <title>{pageTitle}</title>
      <meta name="description" content={pageDesc} />
      <meta name="keywords" content={pageKeywords} />
      <link rel="canonical" href="https://yukafile.github.io/about" />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDesc} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content="https://yukafile.github.io/about" />
      <script type="application/ld+json">
        {JSON.stringify(orgSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(breadcrumbSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(faqSchema)}
      </script>

      <div className="pt-20 md:pt-24 pb-10 md:pb-14 px-4 md:px-6">
        <div className="max-w-[1280px] mx-auto">
          <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '关于我们' }]} />

          <section className="mt-4 md:mt-6">
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-50 mb-4">
              关于星空影院
            </h1>
            <p className="text-sm md:text-base text-foreground-400 leading-relaxed max-w-3xl mb-4">
              星空影院成立于2020年，是一个专注于为用户提供高品质影视内容的在线平台。我们致力于打造一个<strong>免费、高清、无广告</strong>的观影环境，让每一位用户都能轻松享受到优质的电影和剧集内容。
            </p>
            <p className="text-sm md:text-base text-foreground-400 leading-relaxed max-w-3xl">
              经过多年发展，星空影院已经积累了大量忠实用户，月活跃用户超过千万。我们的片库涵盖数万部电影、数千部剧集，覆盖动作、喜剧、剧情、科幻、恐怖、爱情、动画、纪录片等全类型内容。每天还有最新影片持续上架，保证用户始终有新鲜内容可看。
            </p>
          </section>

          <section className="mt-10 md:mt-14">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-50 mb-6 text-center">
              <a href="#features">为什么选择星空影院？</a>
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {features.map((feat) => (
                <div
                  key={feat.title}
                  className="p-5 md:p-6 bg-background-800/40 rounded-xl border border-background-700/30 hover:border-primary-500/30 transition-all duration-300"
                >
                  <div className="w-10 h-10 md:w-11 md:h-11 flex items-center justify-center rounded-lg bg-primary-500/10 mb-4">
                    <i className={`${feat.icon} text-primary-500 text-xl`}></i>
                  </div>
                  <h3 className="font-heading text-base font-semibold text-foreground-100 mb-2">
                    {feat.title}
                  </h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">
                    {feat.desc}
                  </p>
                </div>
              ))}
            </div>
          </section>

          <section className="mt-10 md:mt-14">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
              <div className="p-5 md:p-6 bg-background-800/40 rounded-xl border border-background-700/30 text-center">
                <div className="text-2xl md:text-3xl font-bold text-accent-500 mb-1">1000万+</div>
                <p className="text-sm text-foreground-500">月活跃用户</p>
              </div>
              <div className="p-5 md:p-6 bg-background-800/40 rounded-xl border border-background-700/30 text-center">
                <div className="text-2xl md:text-3xl font-bold text-accent-500 mb-1">5万+</div>
                <p className="text-sm text-foreground-500">影视资源</p>
              </div>
              <div className="p-5 md:p-6 bg-background-800/40 rounded-xl border border-background-700/30 text-center">
                <div className="text-2xl md:text-3xl font-bold text-accent-500 mb-1">99.8%</div>
                <p className="text-sm text-foreground-500">用户好评率</p>
              </div>
              <div className="p-5 md:p-6 bg-background-800/40 rounded-xl border border-background-700/30 text-center">
                <div className="text-2xl md:text-3xl font-bold text-accent-500 mb-1">24h</div>
                <p className="text-sm text-foreground-500">每日更新</p>
              </div>
            </div>
          </section>

          <section className="mt-10 md:mt-14">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-50 mb-6 text-center">
              <a href="#testimonials">用户口碑</a>
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
              {testimonials.map((t) => (
                <div
                  key={t.name}
                  className="p-5 md:p-6 bg-background-800/40 rounded-xl border border-background-700/30"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <img
                      src={t.avatar}
                      alt={t.name}
                      className="w-11 h-11 md:w-12 md:h-12 rounded-full object-cover"
                    />
                    <div>
                      <p className="text-sm font-semibold text-foreground-200">{t.name}</p>
                      <p className="text-xs text-foreground-500">{t.role}</p>
                    </div>
                  </div>
                  <p className="text-sm text-foreground-400 leading-relaxed">{t.comment}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="mt-10 md:mt-14">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-50 mb-6 text-center">
              <a href="#faq">常见问题</a>
            </h2>
            <div className="max-w-3xl mx-auto space-y-3">
              {[
                { q: '星空影院是否需要注册才能观看？', a: '不需要。星空影院在线观看免费，所有影片无需注册、无需付费即可直接观看。我们致力于为用户提供最便捷的观影体验。' },
                { q: '星空影院支持哪些设备播放？', a: '星空影院支持手机、平板、电脑等多种设备播放，画质清晰流畅，自适应不同屏幕尺寸，让您随时随地畅享高清影视内容。' },
                { q: '星空影院的影片更新频率如何？', a: '星空影院每日更新最新电影和热播剧集，确保您第一时间观看到最新上映的精彩内容。我们会持续追踪全球热门影视动态。' },
                { q: '星空影院有哪些影片类型？', a: '星空影院涵盖动作、喜剧、剧情、科幻、恐怖、爱情、动画、纪录片等多种类型，同时还有海量热播剧集和综艺节目供您选择。' },
              ].map((faq, i) => (
                <details key={i} className="group bg-background-800/40 rounded-xl border border-background-700/30 overflow-hidden cursor-pointer">
                  <summary className="flex items-center justify-between p-5 md:p-6 text-sm font-medium text-foreground-200 cursor-pointer select-none">
                    {faq.q}
                    <div className="w-5 h-5 flex items-center justify-center ml-3 flex-shrink-0">
                      <i className="ri-arrow-down-s-line text-foreground-500 group-open:rotate-180 transition-transform duration-200"></i>
                    </div>
                  </summary>
                  <div className="px-5 md:px-6 pb-5 md:pb-6">
                    <p className="text-sm text-foreground-500 leading-relaxed">{faq.a}</p>
                  </div>
                </details>
              ))}
            </div>
          </section>

          <section className="mt-10 md:mt-14">
            <div className="max-w-2xl mx-auto">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-50 mb-6 text-center">
                <a href="#contact">联系我们</a>
              </h2>

              {formSuccess ? (
                <div className="p-8 bg-background-800/40 rounded-xl border border-primary-500/30 text-center">
                  <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-primary-500/20">
                    <i className="ri-check-line text-primary-500 text-2xl"></i>
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-foreground-100 mb-2">提交成功！</h3>
                  <p className="text-sm text-foreground-500 mb-4">感谢您的留言，我们会尽快回复您。</p>
                  <button
                    onClick={() => setFormSuccess(false)}
                    className="px-5 py-2.5 bg-primary-500 text-background-950 rounded-full text-sm font-medium hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
                  >
                    继续留言
                  </button>
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  data-readdy-form
                  className="p-6 md:p-8 bg-background-800/40 rounded-xl border border-background-700/30 space-y-5"
                >
                  <div>
                    <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-300 mb-2">姓名</label>
                    <input
                      id="contact-name"
                      name="name"
                      type="text"
                      required
                      className="w-full px-4 py-3 bg-background-900/80 border border-background-700/50 rounded-lg text-sm text-foreground-200 placeholder-foreground-600 outline-none focus:border-primary-500/50 focus:ring-1 focus:ring-primary-500/20 transition-all duration-200"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-300 mb-2">邮箱</label>
                    <input
                      id="contact-email"
                      name="email"
                      type="email"
                      required
                      className="w-full px-4 py-3 bg-background-900/80 border border-background-700/50 rounded-lg text-sm text-foreground-200 placeholder-foreground-600 outline-none focus:border-primary-500/50 focus:ring-1 focus:ring-primary-500/20 transition-all duration-200"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-300 mb-2">留言内容</label>
                    <textarea
                      id="contact-message"
                      name="message"
                      required
                      maxLength={500}
                      rows={4}
                      className="w-full px-4 py-3 bg-background-900/80 border border-background-700/50 rounded-lg text-sm text-foreground-200 placeholder-foreground-600 outline-none focus:border-primary-500/50 focus:ring-1 focus:ring-primary-500/20 transition-all duration-200 resize-none"
                      placeholder="请描述您的问题或建议（500字以内）"
                    ></textarea>
                  </div>
                  <input
                    type="text"
                    name="phone_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="form-honeypot"
                  />
                  {formError && (
                    <p className="text-sm text-accent-500">{formError}</p>
                  )}
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full py-3 bg-primary-500 hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed text-background-950 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap cursor-pointer"
                  >
                    {submitting ? '提交中...' : '提交留言'}
                  </button>
                </form>
              )}
            </div>
          </section>
        </div>
      </div>
    </>
  );
}

export const runtime = 'edge';